import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  RELAY_DEPOSIT_CHAIN_OPTIONS,
  RelayDepositRecord,
  verifyRelayDeposit,
  VerifyRelayDepositResult,
} from '../../services/relayDepositService';
import { PermissionButton } from 'src/routes/components/permission-button';

type Props = {
  record: RelayDepositRecord;
  open: boolean;
  onClose: () => void;
};

function depositAddressForChain(record: RelayDepositRecord, chainId: number): string {
  if (chainId === 792703809) return record.solDepositAddr || '';
  if (chainId === 728126428) return record.tvmDepositAddr || '';
  return record.depositAddr || '';
}

export default function VerifyDialog({ record, open, onClose }: Props) {
  const t = useTranslate();
  const [chainId, setChainId] = useState<number>(8453);
  const [depositAddress, setDepositAddress] = useState('');
  const [targetChainId, setTargetChainId] = useState<string>('');
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerifyRelayDepositResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const suggestedAddress = useMemo(
    () => depositAddressForChain(record, chainId),
    [record, chainId]
  );

  useEffect(() => {
    if (open) {
      setChainId(8453);
      setDepositAddress(depositAddressForChain(record, 8453));
      setTargetChainId('');
      setCurrency('');
      setResult(null);
      setError(null);
    }
  }, [open, record]);

  const handleChainChange = (nextChainId: number) => {
    setChainId(nextChainId);
    setDepositAddress(depositAddressForChain(record, nextChainId));
    setResult(null);
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await verifyRelayDeposit({
        id: record.id,
        chainId,
        depositAddress: depositAddress.trim() || undefined,
        targetChainId: targetChainId ? Number(targetChainId) : undefined,
        currency: currency.trim() || undefined,
      });
      setResult(res);
    } catch (err: any) {
      const msg =
        err?.body?.message ||
        err?.json?.message ||
        err?.message ||
        t('module.relay-deposit.verifyFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('module.relay-deposit.verifyTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('module.relay-deposit.verifyHint')}
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="relay-verify-chain-label">
              {t('module.relay-deposit.chainId')}
            </InputLabel>
            <Select
              labelId="relay-verify-chain-label"
              label={t('module.relay-deposit.chainId')}
              value={chainId}
              onChange={(e) => handleChainChange(Number(e.target.value))}
            >
              {RELAY_DEPOSIT_CHAIN_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('module.relay-deposit.depositAddress')}
            value={depositAddress}
            onChange={(e) => setDepositAddress(e.target.value)}
            placeholder={suggestedAddress || undefined}
            helperText={
              suggestedAddress
                ? t('module.relay-deposit.depositAddressHint', { address: suggestedAddress })
                : undefined
            }
          />

          <TextField
            fullWidth
            label={t('module.relay-deposit.targetChainId')}
            value={targetChainId}
            onChange={(e) => setTargetChainId(e.target.value)}
            placeholder={String(chainId)}
          />

          <TextField
            fullWidth
            label={t('module.relay-deposit.currency')}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder={t('module.relay-deposit.currencyPlaceholder')}
          />

          {error && <Alert severity="error">{error}</Alert>}

          {result && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {result.message}
              </Alert>
              <Typography variant="subtitle2" gutterBottom>
                {t('module.relay-deposit.verifySummary', {
                  checked: result.checkedCurrencies,
                  failed: result.failedCurrencies,
                  triggered: result.triggeredCurrencies?.length ?? 0,
                })}
              </Typography>
              {(result.triggeredCurrencies?.length ?? 0) > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('module.relay-deposit.currency')}</TableCell>
                      <TableCell>{t('module.relay-deposit.symbol')}</TableCell>
                      <TableCell>{t('module.relay-deposit.balance')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.triggeredCurrencies.map((row) => (
                      <TableRow key={`${row.currency}-${row.balance}`}>
                        <TableCell sx={{ maxWidth: 280, wordBreak: 'break-all' }}>
                          {row.currency}
                        </TableCell>
                        <TableCell>{row.symbol || '-'}</TableCell>
                        <TableCell>{row.balance}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('ra.action.close')}
        </Button>
        <PermissionButton resource="relay" action="update">
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {t('module.relay-deposit.verify')}
          </Button>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
